import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useMount, usePrevious, useUnmount } from "react-use";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Undo } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Divider, Paper, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import MultiLayerEditHandler, { MultiLayerEditHandlerRef } from "fond/draw/MultiLayerEdit/MultiLayerEditHandler";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { EditMode, setEditMode } from "fond/project/redux";
import { ImportStatus, ISGLayerIds, isImportInProgress } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of ISG Blockers.
 */
const BlockersDrawHandler: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setDrawMode } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { goBack } = useStackedNavigationContext();
  const multiLayerEditRef = useRef<MultiLayerEditHandlerRef>(null);

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      editOptions: {
        allLayers: [
          {
            ID: ISGLayerIds.blockers,
            GeometryType: "LineString",
            // Maybe relate what this returns to the attributes defined.
            getFeatureProperties: () => ({ layerId: ISGLayerIds.blockers, Tier1: true, Tier2: false, Tier2_MST: false, Tier3: false }),
            Attributes: {
              Tier1: { Type: "BOOLEAN" },
              Tier2: { Type: "BOOLEAN" },
              Tier2_MST: { Type: "BOOLEAN" },
              Tier3: { Type: "BOOLEAN" },
            },
            PromptAttributesOnCreate: true,
          },
        ],
        editingLayerId: ISGLayerIds.blockers, // Default selected layer
        editingLayerIds: [ISGLayerIds.blockers], // Default editing layers (enables edit mode)
      },
    }),
    []
  );

  const inputLayers = useGetImportLayers(versionId, defaultOptions.editOptions?.allLayers?.map((layer) => layer.ID) || []);
  const isImporting = useMemo(
    () => Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status)),
    [inputLayers]
  );

  const prevIsImporting = usePrevious(isImporting);

  useEffect(() => {
    if (prevIsImporting && !isImporting) {
      // Import has completed (transitioned from true to false)
      // Check if all imports completed successfully (not errors)
      const allComplete = Array.from(inputLayers.values()).every((layer) => !layer.status || layer.status === ImportStatus.COMPLETE);

      if (allComplete) {
        goBack();
      }
    }
  }, [prevIsImporting, isImporting, inputLayers, goBack]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    setDrawMode("no_feature");
    dispatch(setEditMode(EditMode.none));
  });

  const handleBack = useCallback(() => {
    if (isImporting) return; // Prevent going back while import is in progress
    goBack();
  }, [goBack, isImporting]);

  const handleOnSave = useCallback(async () => {
    await multiLayerEditRef.current?.save();
  }, []);

  const handleOnReset = useCallback(() => {
    multiLayerEditRef.current?.reset();
  }, []);

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Draw on map" />
      <Alert severity="info" data-testid="layer-alert">
        <AlertTitle sx={{ mb: 0 }}>Draw blockers</AlertTitle>
      </Alert>
      <Divider sx={{ mt: 3 }} />
      <Button
        fullWidth
        sx={{ my: 0.5, justifyContent: "left" }}
        startIcon={<Undo sx={{ height: 20 }} />}
        onClick={handleOnReset}
        disabled={isImporting}
      >
        <Typography color={(theme) => theme.palette.common.black} fontWeight={500} fontSize={13}>
          Reset
        </Typography>
      </Button>
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack} disabled={isImporting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnSave}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          loading={isImporting}
        >
          Finish
        </LoadingButton>
      </Box>
      <MultiLayerEditHandler
        ref={multiLayerEditRef}
        versionId={versionId}
        options={defaultOptions}
        config={allToolConfig}
        startingMode="draw_line_string"
      />

      {isImporting && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default BlockersDrawHandler;
